/* ============================================================================
   Landing surface · the immersive search bar.
   Fast typeahead over /v1/operators, with curated brand-groups surfaced first.
   Architected so an LLM intent-resolver can later sit behind the same input.
   ========================================================================== */

import {
  getCycleMap,
  getOperatorIndex,
  getReceiverIndex,
  getVesselFeed,
  type OperatorHit,
  type ReceiverLite,
  type VesselFeedItem,
} from "./api";
import { clear, eq, h, icon } from "./dom";
import { GROUPS, matchGroups, type BrandGroup } from "./groups";
import { titleCase } from "./format";
import logoUrl from "../img/phonic_logo_nobg.svg";

type Nav = (path: string) => void;

interface Row {
  kind: "group" | "operator" | "vessel" | "location" | "owner";
  navTo: string;
  el: HTMLElement;
}

interface OwnerHit {
  org: string;
  name: string;
  count: number;
}

export function renderLanding(root: HTMLElement, nav: Nav): void {
  clear(root);

  const input = h("input", {
    type: "text",
    placeholder: "Search a company, vessel, location or owner…",
    autocomplete: "off",
    autocapitalize: "off",
    spellcheck: "false",
    "aria-label": "Search",
  }) as HTMLInputElement;

  const results = h("div", { class: "results", role: "listbox" });

  const box = h(
    "div",
    { class: "search-box" },
    h("span", { class: "glyph" }, icon("search", 20)),
    input,
  );

  const surpriseEl = h(
    "button",
    { class: "surprise", type: "button", onClick: () => void surpriseNav() },
    "Surprise me",
  );

  const search = h(
    "div",
    { class: "search" },
    box,
    results,
    h(
      "div",
      { class: "search-scopes" },
      surpriseEl,
    ),
  );

  const landing = h(
    "section",
    { class: "landing" },
    h(
      "div",
      { class: "landing-inner" },
      h(
        "div",
        { class: "landing-logo" },
        h("img", { src: logoUrl, alt: "Phoniq", width: "120", height: "120" }),
      ),
      search,
    ),
    h(
      "div",
      { class: "landing-foot" },
      "Live data · BarentsWatch · Fiskeridirektoratet · AIS · Kystverket",
    ),
  );

  root.append(landing);
  input.focus();

  // ---- typeahead state ---------------------------------------------------
  let rows: Row[] = [];
  let active = -1;
  let seq = 0;
  // Prefetched, cached search indexes (filtered client-side → instant search).
  let opIndexCache: Promise<Map<string, OperatorHit>> | null = null;
  let feedCache: Promise<VesselFeedItem[]> | null = null;
  let rcvIndexCache: Promise<Map<number, ReceiverLite>> | null = null;
  const warmIndexes = () => {
    // Forget a failed index so the next keystroke retries it. Holding on to the
    // rejected promise would leave search permanently "unavailable" for this
    // page even after the API recovered a second later.
    if (!opIndexCache) {
      const p = (opIndexCache = getOperatorIndex());
      p.catch(() => {
        if (opIndexCache === p) opIndexCache = null;
      });
    }
    feedCache ??= getVesselFeed().catch(() => [] as VesselFeedItem[]);
    rcvIndexCache ??= getReceiverIndex().catch(() => new Map<number, ReceiverLite>());
  };
  warmIndexes(); // start loading immediately on mount

  // "Surprise me" · jump to a random entity, balanced across the four types.
  async function surpriseNav(): Promise<void> {
    warmIndexes();
    try {
      const [index, feed, rcv] = await Promise.all([opIndexCache!, feedCache!, rcvIndexCache!]);
      const pick = <T>(a: T[]): T | undefined => a[Math.floor(Math.random() * a.length)];
      const types = ["group", "company", "vessel", "location", "owner"];
      for (let i = 0; i < 8; i++) {
        switch (pick(types)) {
          case "group": {
            const g = pick(GROUPS);
            if (g) return nav(`/g/${g.slug}`);
            break;
          }
          case "company": {
            const o = pick([...index.values()]);
            if (o) return nav(`/c/${o.slug}`);
            break;
          }
          case "vessel": {
            const v = pick(feed.filter((x) => x.name));
            if (v) return nav(`/v/${v.mmsi}`);
            break;
          }
          case "location": {
            // Prefer active sites — a fallow site is a dull surprise. Mostly
            // pick from sites with standing biomass, occasionally any.
            const cycle = await getCycleMap().catch(() => null);
            const all = [...rcv.values()];
            const active = cycle
              ? all.filter((l) => l.locality_no != null && (cycle.get(l.locality_no)?.est_biomass_t ?? 0) > 0)
              : all;
            const l = pick(active.length && Math.random() < 0.85 ? active : all);
            if (l) return nav(`/l/${l.locality_no}`);
            break;
          }
          case "owner": {
            const og = pick([...new Set(feed.map((x) => x.owner_org).filter(Boolean))]);
            if (og) return nav(`/o/${og}`);
            break;
          }
        }
      }
    } catch {
      /* indexes unavailable — do nothing */
    }
  }

  const go = (i: number) => {
    if (rows[i]) nav(rows[i].navTo);
  };

  const setActive = (i: number) => {
    active = i;
    rows.forEach((r, idx) => r.el.setAttribute("aria-selected", String(idx === active)));
    rows[active]?.el.scrollIntoView({ block: "nearest" });
  };

  const showSuggestions = () => {
    clear(results);
    const suggest = GROUPS.slice(0, 4);
    results.append(groupLabel("Major producers"));
    rows = suggest.map((g) => {
      const el = groupRow(g);
      results.append(el);
      return { kind: "group" as const, navTo: `/g/${g.slug}`, el };
    });
    bindRowClicks();
  };

  const render = (
    groups: BrandGroup[],
    ops: OperatorHit[],
    vessels: VesselFeedItem[],
    locations: ReceiverLite[],
    owners: OwnerHit[],
  ) => {
    clear(results);
    rows = [];
    if (groups.length) {
      results.append(groupLabel("Groups · aggregated"));
      for (const g of groups) {
        const el = groupRow(g);
        results.append(el);
        rows.push({ kind: "group", navTo: `/g/${g.slug}`, el });
      }
    }
    if (ops.length) {
      results.append(groupLabel("Companies"));
      for (const op of ops) {
        const el = operatorRow(op);
        results.append(el);
        rows.push({ kind: "operator", navTo: `/c/${op.slug}`, el });
      }
    }
    if (vessels.length) {
      results.append(groupLabel("Vessels"));
      for (const v of vessels) {
        const el = vesselRow(v);
        results.append(el);
        rows.push({ kind: "vessel", navTo: `/v/${v.mmsi}`, el });
      }
    }
    if (locations.length) {
      results.append(groupLabel("Locations"));
      for (const l of locations) {
        const el = locationRow(l);
        results.append(el);
        rows.push({ kind: "location", navTo: `/l/${l.locality_no}`, el });
      }
    }
    if (owners.length) {
      results.append(groupLabel("Vessel owners"));
      for (const o of owners) {
        const el = ownerRow(o);
        results.append(el);
        rows.push({ kind: "owner", navTo: `/o/${o.org}`, el });
      }
    }
    if (!rows.length) {
      results.append(h("div", { class: "results-empty" }, "No matches. Try a company, vessel or site name."));
    }
    active = -1;
    bindRowClicks();
  };

  const bindRowClicks = () => {
    rows.forEach((r, idx) => {
      r.el.addEventListener("mouseenter", () => setActive(idx));
      r.el.addEventListener("click", () => go(idx));
    });
  };

  const run = async (q: string) => {
    const mine = ++seq;
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      showSuggestions();
      return;
    }
    const lc = trimmed.toLowerCase();
    const groups = matchGroups(trimmed);

    // Both indexes are fetched once and cached, so searching is client-side and
    // instant · no per-keystroke network round-trip. Only the very first query
    // (cold cache) shows the shimmer.
    const warm = opIndexCache && feedCache;
    if (!warm) {
      clear(results);
      results.append(h("div", { class: "results-loading" }, eq(4), " Searching…"));
    }
    try {
      warmIndexes();
      const [index, feed, rcv] = await Promise.all([opIndexCache!, feedCache!, rcvIndexCache!]);
      if (mine !== seq) return; // stale

      const groupOrgs = new Set(groups.flatMap((g) => g.orgNrs));
      const ops = [...index.values()]
        .filter((o) => o.name.toLowerCase().includes(lc) && !groupOrgs.has(o.org_nr))
        .sort((a, b) => relevance(a.name, lc) - relevance(b.name, lc) || b.n_sites - a.n_sites)
        .slice(0, 7);
      const vessels = feed
        .filter((v) => v.name && v.name.toLowerCase().includes(lc))
        .sort((a, b) => relevance(a.name ?? "", lc) - relevance(b.name ?? "", lc))
        .slice(0, 6);
      const locations = [...rcv.values()]
        .filter((l) => l.name.toLowerCase().includes(lc))
        .sort((a, b) => relevance(a.name, lc) - relevance(b.name, lc))
        .slice(0, 5);
      // Owners: aggregate the feed by owner org, match on owner name.
      const ownerMap = new Map<string, OwnerHit>();
      for (const v of feed) {
        if (!v.owner || !v.owner_org || !v.owner.toLowerCase().includes(lc)) continue;
        const e = ownerMap.get(v.owner_org) ?? { org: v.owner_org, name: v.owner, count: 0 };
        e.count++;
        ownerMap.set(v.owner_org, e);
      }
      const owners = [...ownerMap.values()]
        .sort((a, b) => relevance(a.name, lc) - relevance(b.name, lc) || b.count - a.count)
        .slice(0, 5);
      render(groups, ops, vessels, locations, owners);
    } catch {
      if (mine !== seq) return;
      clear(results);
      results.append(h("div", { class: "results-empty" }, "Search is unavailable right now."));
    }
  };

  let timer: number | undefined;
  input.addEventListener("input", () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => run(input.value), 130);
  });
  input.addEventListener("focus", () => {
    if (input.value.trim().length < 2) showSuggestions();
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(Math.min(active + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(Math.max(active - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(active >= 0 ? active : 0);
    } else if (e.key === "Escape") {
      clear(results);
    }
  });

  document.addEventListener("click", (e) => {
    if (!search.contains(e.target as Node)) clear(results);
  });
}

/* ---- row builders -------------------------------------------------------- */

function groupLabel(text: string): HTMLElement {
  return h("div", { class: "result-group-label eyebrow" }, text);
}

function groupRow(g: BrandGroup): HTMLElement {
  return h(
    "div",
    { class: "result", role: "option" },
    h("span", { class: "kind" }, icon("layers", 15)),
    h(
      "div",
      { class: "body" },
      h("div", { class: "name" }, g.name),
      h("div", { class: "meta" }, `Group · ${g.orgNrs.length} legal entities · aggregated`),
    ),
    h("div", { class: "stat" }, h("b", {}, "Group"), "view"),
  );
}

function ownerRow(o: OwnerHit): HTMLElement {
  return h(
    "div",
    { class: "result", role: "option" },
    h("span", { class: "kind" }, icon("building", 15)),
    h(
      "div",
      { class: "body" },
      h("div", { class: "name" }, titleCase(o.name)),
      h("div", { class: "meta" }, "Vessel owner · NOR/NIS register"),
    ),
    h("div", { class: "stat" }, h("b", {}, String(o.count)), o.count === 1 ? "vessel" : "vessels"),
  );
}

function locationRow(l: ReceiverLite): HTMLElement {
  return h(
    "div",
    { class: "result", role: "option" },
    h("span", { class: "kind" }, icon("pin", 15)),
    h(
      "div",
      { class: "body" },
      h("div", { class: "name" }, titleCase(l.name)),
      h("div", { class: "meta" }, `Site · locality ${l.locality_no}`),
    ),
    h("div", { class: "stat" }, h("b", {}, "Site"), "view"),
  );
}

/** Lower is better: prefix match beats word-start beats substring. */
function relevance(name: string, lc: string): number {
  const n = name.toLowerCase();
  if (n.startsWith(lc)) return 0;
  if (n.split(/[\s/]+/).some((w) => w.startsWith(lc))) return 1;
  return 2;
}

function vesselRow(v: VesselFeedItem): HTMLElement {
  const role = v.role ? titleCase(v.role) : "Vessel";
  const meta = [role, v.length_m ? `${Math.round(v.length_m)} m` : null]
    .filter(Boolean)
    .join(" · ");
  return h(
    "div",
    { class: "result", role: "option" },
    h("span", { class: "kind" }, icon("ship", 15)),
    h(
      "div",
      { class: "body" },
      h("div", { class: "name" }, titleCase(v.name ?? `MMSI ${v.mmsi}`)),
      h("div", { class: "meta" }, meta || `MMSI ${v.mmsi}`),
    ),
    h("div", { class: "stat" }, h("b", {}, "Vessel"), "view"),
  );
}

function operatorRow(op: OperatorHit): HTMLElement {
  const place = [op.municipality, op.county]
    .filter((x): x is string => !!x)
    .map(titleCase)
    .join(" · ");
  return h(
    "div",
    { class: "result", role: "option" },
    h("span", { class: "kind" }, icon("building", 15)),
    h(
      "div",
      { class: "body" },
      h("div", { class: "name" }, titleCase(op.name)),
      h("div", { class: "meta" }, place || op.entity_type),
    ),
    h("div", { class: "stat" }, h("b", {}, String(op.n_sites)), "sites"),
  );
}
